import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CountdownTimerComponent } from '../../../types/builder';
import { ColorPicker } from '../../Common/ColorPicker';
import { ToggleSwitch } from '../../Common/ToggleSwitch';

interface CountdownTimerSettingsProps {
  component: CountdownTimerComponent;
  onUpdate: (settings: CountdownTimerComponent['settings']) => void;
}

export const CountdownTimerSettings: React.FC<CountdownTimerSettingsProps> = ({ component, onUpdate }) => {
  const { t } = useTranslation('builder');
  const { settings } = component;

  const handleChange = (field: string, value: any) => {
    onUpdate({
      ...settings,
      [field]: value,
    });
  };

  const handleLabelChange = (labelField: keyof CountdownTimerComponent['settings']['labels'], value: string) => {
    onUpdate({
      ...settings,
      labels: {
        ...settings.labels,
        [labelField]: value,
      },
    });
  };

  const formatDateForInput = (isoDate: string) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.title')}
        </label>
        <input
          type="text"
          value={settings.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.countdownTimer.targetDate')}
        </label>
        <input
          type="datetime-local"
          value={formatDateForInput(settings.targetDate)}
          onChange={(e) => handleChange('targetDate', new Date(e.target.value).toISOString())}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('settings.countdownTimer.displayOptions')}
        </label>

        <ToggleSwitch
          label={t('settings.countdownTimer.showDays')}
          checked={settings.showDays}
          onChange={(checked) => handleChange('showDays', checked)}
        />

        <ToggleSwitch
          label={t('settings.countdownTimer.showHours')}
          checked={settings.showHours}
          onChange={(checked) => handleChange('showHours', checked)}
        />

        <ToggleSwitch
          label={t('settings.countdownTimer.showMinutes')}
          checked={settings.showMinutes}
          onChange={(checked) => handleChange('showMinutes', checked)}
        />

        <ToggleSwitch
          label={t('settings.countdownTimer.showSeconds')}
          checked={settings.showSeconds}
          onChange={(checked) => handleChange('showSeconds', checked)}
        />
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          {t('settings.countdownTimer.labels')}
        </label>

        {settings.showDays && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              {t('settings.countdownTimer.daysLabel')}
            </label>
            <input
              type="text"
              value={settings.labels.days}
              onChange={(e) => handleLabelChange('days', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
            />
          </div>
        )}

        {settings.showHours && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              {t('settings.countdownTimer.hoursLabel')}
            </label>
            <input
              type="text"
              value={settings.labels.hours}
              onChange={(e) => handleLabelChange('hours', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
            />
          </div>
        )}

        {settings.showMinutes && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              {t('settings.countdownTimer.minutesLabel')}
            </label>
            <input
              type="text"
              value={settings.labels.minutes}
              onChange={(e) => handleLabelChange('minutes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
            />
          </div>
        )}

        {settings.showSeconds && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              {t('settings.countdownTimer.secondsLabel')}
            </label>
            <input
              type="text"
              value={settings.labels.seconds}
              onChange={(e) => handleLabelChange('seconds', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.alignment')}
        </label>
        <select
          value={settings.alignment}
          onChange={(e) => handleChange('alignment', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        >
          <option value="left">{t('settings.common.alignLeft')}</option>
          <option value="center">{t('settings.common.alignCenter')}</option>
          <option value="right">{t('settings.common.alignRight')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.backgroundColor')}
        </label>
        <ColorPicker
          color={settings.backgroundColor}
          onChange={(color) => handleChange('backgroundColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.textColor')}
        </label>
        <ColorPicker
          color={settings.textColor}
          onChange={(color) => handleChange('textColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.countdownTimer.timerColor')}
        </label>
        <ColorPicker
          color={settings.timerColor}
          onChange={(color) => handleChange('timerColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.countdownTimer.boxBackgroundColor')}
        </label>
        <ColorPicker
          color={settings.boxBackgroundColor}
          onChange={(color) => handleChange('boxBackgroundColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.padding')}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={settings.padding}
          onChange={(e) => handleChange('padding', parseInt(e.target.value))}
          className="w-full"
        />
        <div className="text-sm text-gray-600 text-center mt-1">{settings.padding}px</div>
      </div>
    </div>
  );
};
